import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Route, Switch, useRouteMatch, useHistory } from 'react-router-dom';

import { NAV_DRAWER_ITEMS } from '~/features/dashboard';
import { PageTitle } from '~/features/dashboard/components/page-title';
import { SongDetails } from '~/features/dashboard/components/song-details';
import { SongsList } from '~/features/dashboard/components/songs-list';
import { selectMusicList } from '~/features/dashboard/store/selectors';
import * as variables from '~/styles/variables.scss';
import { useHeaderColorChanger } from '~/utils/hooks/useHeaderColorChanger';
import css from './styles.module.scss';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { getMusicsThunk } from '~/features/dashboard/store/actions.thunks';
import { isSongFormEditing } from '~/features/dashboard/store/actions';
import { Loader } from '~/common/loader';
import { SongsGrid } from '~/features/dashboard/components/songs-grid';
import { DownloadButton } from '~/common/download-button';
import classNames from 'classnames';
import { MusicApi } from '~/api/music.api';
import { useAppRoute } from '~/utils/hooks/useAppRoute';
import { AddSong } from '~/features/dashboard/components/add-song';
import { PreviewSongReport } from '~/features/dashboard/components/preview-song-report';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
    IconButton,
    List,
    ListItem,
    ListItemText,
    Popover
} from '@material-ui/core';
import ListIcon from '@material-ui/icons/List';
import AppsIcon from '@material-ui/icons/Apps';
import { QuickSongEntry } from '~/features/dashboard/components/quick-song-entry';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import AppBar from '@material-ui/core/AppBar';
interface MyDealsProps {}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        popover: {
            pointerEvents: 'none',
        },
        popoverContent: {
            pointerEvents: 'auto',
        },
        root: {
            width: '100%',
            maxWidth: 360,
            backgroundColor: theme.palette.background.paper,
        },
    })
);

export const MySongs: React.FC<MyDealsProps> = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [viewStyle, setViewStyle] = useState<'grid' | 'list'>('grid');
    const [value, setValue] = React.useState(0);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    function a11yProps(index: any) {
        return {
            id: `tab-${index}`,
            'aria-controls': `tabpanel-${index}`,
        };
    }

    const config = NAV_DRAWER_ITEMS.songs;
    const match = useRouteMatch<{ creatorId: string }>();
    const history = useHistory();
    const dispatch = useThunkDispatch();
    const route = useAppRoute();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const togglePopover = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };
    const open = Boolean(anchorEl);

    const classes = useStyles();

    useEffect(() => {
        if (match.params.creatorId) {
            dispatch(getMusicsThunk({ creatorId: Number(match.params.creatorId) })).finally(() => setIsLoading(false));
        }
    }, [dispatch, match.params.creatorId]);
    const musics = useSelector(selectMusicList);

    useHeaderColorChanger(config.background, variables.colorBlueText);
    const goToNewSongPage = () => {
        history.push({
            pathname: `${match.url}/add-song`,
            state: {
                gotoStep: '',
            },
        });
    };
    const goToQuickSongEntry = () => {
        history.push({
            pathname: `${match.url}/quick-song-entry`,
        });
    };
    const toggleViewStyle = () => {
        setViewStyle(prevState => (prevState === 'grid' ? 'list' : 'grid'));
    };

    const isSongsTable = useRouteMatch({ path: `${match.path}/table`, exact: true });
    const isNewSong = useRouteMatch({ path: `${match.path}/add-song`, exact: true });

    const [rawFilters, setFilters] = useState<{ search?: string; startDate?: string; endDate?: string }>();

    const handleDownloadClick = useCallback(() => {
        if (match.params.creatorId) {
            const filters: { projectId: number; searchText?: string; releasedatefrom?: string; releasedateto?: string } = {
                projectId: Number(match.params.creatorId),
            };

            if (rawFilters) {
                if (rawFilters.search) {
                    filters.searchText = encodeURIComponent(rawFilters.search);
                }
                if (rawFilters.startDate) {
                    filters.releasedatefrom = rawFilters.startDate;
                }
                if (rawFilters.endDate) {
                    filters.releasedateto = rawFilters.endDate;
                }
            }

            MusicApi.exportMusics(filters).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'].split('; ')[0] }));
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.download = 'download.zip';
                link.click();
                window.URL.revokeObjectURL(url);
                link.remove();
            });
        }
    }, [match.params.creatorId, rawFilters]);

    React.useEffect(() => {
        dispatch(isSongFormEditing(false));
    }, [dispatch]);

    const goToPreviewSong = useCallback(() => route(`${match.url}/preview-song-report`), [match.url, route]);
    return isLoading ? (
        <Loader />
    ) : (
        <article className={css.module}>
            {!!isSongsTable || !!isNewSong ? (
                <div className={classNames(css.topBar, viewStyle === 'grid' && css.pr100)}>
                    {isSongsTable && (
                        <div className={css.topBarActions} onClick={() => dispatch(isSongFormEditing(false))}>
                            {viewStyle === 'list' ? (
                                <AppBar className={css.appTop} position="static">
                                    <Tabs value={value} className={css.topBar} onChange={handleChange} aria-label="simple tabs example">
                                        <Tab label="My Song" {...a11yProps(0)} />
                                        <Tab label="Synch" {...a11yProps(1)} />
                                        <Tab label="Statuses" {...a11yProps(2)} />
                                        <Tab label="DSPs" {...a11yProps(3)} />
                                    </Tabs>
                                </AppBar>
                            ) : (
                                <PageTitle noBorder className={css.songmaintitle} subtitle={isNewSong && 'Create a new song'}>
                                    My Songs ({musics.length})
                                </PageTitle>
                            )}

                            <IconButton
                                aria-label="add to favorites"
                                aria-owns={open ? 'mouse-over-popover' : undefined}
                                aria-haspopup="true"
                                className={css.colorBtn}
                                onClick={togglePopover}
                            >
                                Add New Song
                            </IconButton>
                            <Popover
                                id="mouse-over-popover"
                                className={classes.popover}
                                classes={{
                                    paper: classes.popoverContent,
                                }}
                                open={open}
                                anchorEl={anchorEl}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                onClose={togglePopover}
                                disableRestoreFocus
                                PaperProps={{ onMouseLeave: togglePopover }}
                            >
                                <List component="nav" className={classes.root} aria-label="contacts">
                                    <ListItem
                                        button
                                        onClick={e => {
                                            goToNewSongPage();
                                            togglePopover(e);
                                        }}
                                    >
                                        <ListItemText primary="Add New Song" />
                                    </ListItem>
                                    <ListItem
                                        button
                                        onClick={e => {
                                            goToQuickSongEntry();
                                            togglePopover(e);
                                        }}
                                    >
                                        <ListItemText primary="Quick Song Entry" />
                                    </ListItem>
                                </List>
                            </Popover>
                            <h3 className="grid-listbtn" onClick={toggleViewStyle} style={{ marginLeft: '1rem', cursor: 'pointer' }}>
                                {viewStyle === 'list' ? (
                                    <>
                                        <AppsIcon style={{ verticalAlign: 'middle' }} />
                                        Grid
                                    </>
                                ) : (
                                    <>
                                        <ListIcon style={{ verticalAlign: 'middle' }} />
                                        List
                                    </>
                                )}{' '}
                                View
                            </h3>
                            <DownloadButton onClick={handleDownloadClick} className={classNames(css.downloadButton, css.actionSection)} />
                            <DownloadButton
                                className={classNames(css.downloadButton, css.actionSection)}
                                onClick={() => goToPreviewSong()}
                                buttonFor="preview"
                            />
                        </div>
                    )}
                </div>
            ) : null}
            <Switch>
                <Route exact path={`${match.path}/table`}>
                    {viewStyle === 'list' && <SongsList songs={musics} setFilters={setFilters} page={value} />}
                    {viewStyle === 'grid' && <SongsGrid songs={musics} setFilters={setFilters} />}
                </Route>
                <Route exact path={`${match.path}/table/:songId/details`} component={SongDetails} />
                <Route path={`${match.path}/add-song`}>
                    <AddSong />
                </Route>
                <Route path={`${match.path}/quick-song-entry`}>
                    <QuickSongEntry />
                </Route>
                <Route path={`${match.path}/preview-song-report`}>
                    <PreviewSongReport />
                </Route>
                <Route exact path={match.path}>
                    <Redirect to={`${match.url}/table`} />
                </Route>
            </Switch>
        </article>
    );
};
